import { useMemo } from "react";

/**
 * chartTypeValidator.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Production-grade chart type compatibility engine for Geneilytics.
 *
 * Architecture:
 *   1. CHART_DESCRIPTORS    — capability flags for every supported chart type
 *   2. profileChartData()   — inspects raw chartData → DataProfile
 *   3. checkCompatibility() — DataProfile × ChartType → CompatibilityResult
 *   4. ChartValidator class — public API consumed by ChartRenderer
 *   5. useChartValidator()  — React hook (memoized)
 *
 * Adding a new chart type:
 *   a. Add its descriptor to CHART_DESCRIPTORS with appropriate flags
 *   b. No other file needs to change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── 1. CHART TYPE DESCRIPTORS ───────────────────────────────────────────────

export const CHART_DESCRIPTORS = {
  bar: {
    label: "Bar",
    requiresNumericXY:    false,
    requiresXYPairs:      false,
    requiresLabels:       true,   // needs labels[] for the category axis
    supportsMultiSeries:  true,
    supportsSingleSeries: true,
    supportsCategorical:  true,
    supportsNumericX:     false,
  },
  line: {
    label: "Line",
    requiresNumericXY:    false,
    requiresXYPairs:      false,
    requiresLabels:       true,
    supportsMultiSeries:  true,
    supportsSingleSeries: true,
    supportsCategorical:  true,
    supportsNumericX:     false,
  },
  area: {
    label: "Area",
    requiresNumericXY:    false,
    requiresXYPairs:      false,
    requiresLabels:       true,
    supportsMultiSeries:  true,
    supportsSingleSeries: true,
    supportsCategorical:  true,
    supportsNumericX:     false,
  },
  pie: {
    label: "Pie",
    requiresNumericXY:    false,
    requiresXYPairs:      false,
    requiresLabels:       true,
    supportsMultiSeries:  false,  // single series only
    supportsSingleSeries: true,
    supportsCategorical:  true,
    supportsNumericX:     false,
  },
  doughnut: {
    label: "Doughnut",
    requiresNumericXY:    false,
    requiresXYPairs:      false,
    requiresLabels:       true,
    supportsMultiSeries:  false,
    supportsSingleSeries: true,
    supportsCategorical:  true,
    supportsNumericX:     false,
  },
  scatter: {
    label: "Scatter",
    requiresNumericXY:    true,   // MUST have numeric .x on every point
    requiresXYPairs:      true,   // data must be [{x, y}, …]
    requiresLabels:       false,
    supportsMultiSeries:  true,
    supportsSingleSeries: true,
    supportsCategorical:  false,
    supportsNumericX:     true,
  },
  bubble: {
    label: "Bubble",
    requiresNumericXY:    true,
    requiresXYPairs:      true,
    requiresRField:       true,   // data must be [{x, y, r}, …]
    requiresLabels:       false,
    supportsMultiSeries:  false,
    supportsSingleSeries: true,
    supportsCategorical:  false,
    supportsNumericX:     true,
  },
};

// ─── 2. DATA PROFILER ────────────────────────────────────────────────────────

/**
 * @typedef {Object} DataProfile
 * @property {boolean} hasCategoricalX  — labels[] has non-numeric strings
 * @property {boolean} hasLabels        — labels[] is non-empty (regardless of type)
 * @property {boolean} hasNumericX      — every series point has a numeric .x field
 * @property {boolean} hasXYPairs       — data is [{x, y}, …] shape
 * @property {boolean} hasOnlyXYPairs   — XY pairs present AND no usable labels[]
 *                                        → pure scatter/bubble source, incompatible
 *                                        with any categorical chart type
 * @property {boolean} hasRField        — data is [{x, y, r}, …] (bubble)
 * @property {boolean} isMultiSeries    — series object has > 1 key
 * @property {boolean} isSingleSeries   — values[] or series with 1 key
 * @property {number}  seriesCount
 * @property {number}  pointCount
 */

/**
 * Pure function — inspects a raw chartData payload and returns a DataProfile.
 * @param {Object} chartData
 * @returns {DataProfile}
 */
export function profileChartData(chartData) {
  if (!chartData) return _emptyProfile();

  const { labels = [], values = [], series } = chartData;

  const seriesKeys  = series ? Object.keys(series) : [];
  const seriesCount = seriesKeys.length;
  const allPoints   = _collectAllPoints(series, values);
  const pointCount  = allPoints.length;

  // ── X-axis character ────────────────────────────────────────────
  const hasLabels        = labels.length > 0;
  const hasCategoricalX  = hasLabels && labels.some(l => isNaN(Number(l)));

  const hasNumericX =
    pointCount > 0 &&
    allPoints.every(
      p => typeof p === "object" && p !== null &&
           typeof p.x === "number" && !isNaN(p.x)
    );

  const hasXYPairs =
    pointCount > 0 &&
    allPoints.every(
      p => typeof p === "object" && p !== null &&
           p.x !== undefined && p.y !== undefined
    );

  // KEY FLAG: data is XY-pair only with no usable category labels.
  // This means the dataset came from a scatter/bubble query and cannot
  // be meaningfully rendered as Bar / Line / Pie / Doughnut etc.
  const hasOnlyXYPairs = hasXYPairs && !hasLabels;

  const hasRField = hasXYPairs && allPoints.every(p => p.r !== undefined);

  const isMultiSeries = seriesCount > 1;
  const isSingleSeries = seriesCount <= 1;

  return {
    hasCategoricalX,
    hasLabels,
    hasNumericX,
    hasXYPairs,
    hasOnlyXYPairs,
    hasRField,
    isMultiSeries,
    isSingleSeries,
    seriesCount,
    pointCount,
  };
}

function _collectAllPoints(series, values) {
  if (series) return Object.values(series).flat();
  return values;
}

function _emptyProfile() {
  return {
    hasCategoricalX:  false,
    hasLabels:        false,
    hasNumericX:      false,
    hasXYPairs:       false,
    hasOnlyXYPairs:   false,
    hasRField:        false,
    isMultiSeries:    false,
    isSingleSeries:   true,
    seriesCount:      0,
    pointCount:       0,
  };
}

// ─── 3. COMPATIBILITY ENGINE ─────────────────────────────────────────────────

/**
 * @typedef {Object} CompatibilityResult
 * @property {boolean}            valid
 * @property {'ok'|'invalid'|'warning'} status
 * @property {string}             reason         — shown in UI
 * @property {string|null}        autoConvertTo  — suggested fallback chart type
 */

/**
 * Returns whether targetChartType is compatible with the given DataProfile.
 * Rules are evaluated in priority order — first match wins.
 *
 * @param {string}      targetChartType
 * @param {DataProfile} profile
 * @returns {CompatibilityResult}
 */
export function checkCompatibility(targetChartType, profile) {
  const descriptor = CHART_DESCRIPTORS[targetChartType];

  if (!descriptor) {
    return {
      valid: false,
      status: "invalid",
      reason: `Unknown chart type: "${targetChartType}".`,
      autoConvertTo: null,
    };
  }

  // ── Rule 1 (NEW — core fix): Categorical charts need labels[].
  //    If the data is pure XY-pairs (scatter/bubble source) there are no
  //    category labels to put on the X-axis → Bar / Line / Pie etc. are invalid.
  if (descriptor.requiresLabels && profile.hasOnlyXYPairs) {
    return {
      valid: false,
      status: "invalid",
      reason: `${descriptor.label} charts require category labels on the X-axis. This dataset only has numeric X/Y pairs (scatter data) and cannot be converted to a categorical chart.`,
      autoConvertTo: "scatter",
    };
  }

  // ── Rule 2: Scatter / Bubble require XY pairs ──────────────────
  if (descriptor.requiresXYPairs && !profile.hasXYPairs) {
    return {
      valid: false,
      status: "invalid",
      reason: `${descriptor.label} charts require numeric X/Y data pairs. This dataset uses category labels on the X-axis and is incompatible.`,
      autoConvertTo: "bar",
    };
  }

  // ── Rule 3: Scatter / Bubble require numeric X ─────────────────
  if (descriptor.requiresNumericXY && !profile.hasNumericX) {
    return {
      valid: false,
      status: "invalid",
      reason: `${descriptor.label} charts require all data points to have a numeric X value. Current data has a categorical or missing X axis.`,
      autoConvertTo: "bar",
    };
  }

  // ── Rule 4: Bubble additionally requires an R field ────────────
  if (descriptor.requiresRField && !profile.hasRField) {
    return {
      valid: false,
      status: "invalid",
      reason: `Bubble charts require a radius field (r) on each data point. Your data does not include bubble sizing information.`,
      autoConvertTo: profile.hasXYPairs ? "scatter" : "bar",
    };
  }

  // ── Rule 5: Pie / Doughnut don't support multi-series ──────────
  if (!descriptor.supportsMultiSeries && profile.isMultiSeries) {
    return {
      valid: false,
      status: "invalid",
      reason: `${descriptor.label} charts only support a single data series. Your dataset has ${profile.seriesCount} series — use Bar or Line for multi-series comparison.`,
      autoConvertTo: "bar",
    };
  }

  // ── Rule 6: Categorical-X check (belt-and-suspenders) ──────────
  if (!descriptor.supportsCategorical && profile.hasCategoricalX && !profile.hasNumericX) {
    return {
      valid: false,
      status: "invalid",
      reason: `${descriptor.label} charts do not support text-based X-axis labels.`,
      autoConvertTo: "bar",
    };
  }

  // ── Rule 7: Edge case — empty dataset ─────────────────────────
  if (profile.pointCount === 0) {
    return {
      valid: true,
      status: "warning",
      reason: "No data points available to render.",
      autoConvertTo: null,
    };
  }

  return { valid: true, status: "ok", reason: "", autoConvertTo: null };
}

// ─── 4. PUBLIC CHART VALIDATOR CLASS ─────────────────────────────────────────

export class ChartValidator {
  /** @param {Object} chartData — raw chartData from the backend/state */
  constructor(chartData) {
    this.profile = profileChartData(chartData);
  }

  /**
   * All chart type options enriched with validity info.
   * Used by the dropdown to render disabled/enabled items.
   * @returns {Array<{value, label, valid, reason, status}>}
   */
  getValidOptions() {
    return Object.entries(CHART_DESCRIPTORS).map(([value, descriptor]) => {
      const result = checkCompatibility(value, this.profile);
      return {
        value,
        label:  descriptor.label,
        valid:  result.valid,
        reason: result.reason,
        status: result.status,
      };
    });
  }

  /** @param {string} chartType @returns {CompatibilityResult} */
  validate(chartType) {
    return checkCompatibility(chartType, this.profile);
  }

  /** Returns chartType if valid, else the auto-convert fallback. */
  safeChartType(chartType) {
    const result = this.validate(chartType);
    if (result.valid) return chartType;
    return result.autoConvertTo || "scatter";
  }

  /** @returns {boolean} */
  isValid(chartType) {
    return this.validate(chartType).valid;
  }
}

// ─── 5. REACT HOOK ───────────────────────────────────────────────────────────

/**
 * Returns a ChartValidator memoized on chartData.
 * @param {Object} chartData
 * @returns {ChartValidator}
 */
export function useChartValidator(chartData) {
  return useMemo(() => new ChartValidator(chartData), [chartData]);
}